/**
 * Admin deep links (no react-router — path-based detection, like `/sell` and
 * the DocuSign return routes in App.js).
 *
 *   /admin/ownership-review?case={verificationId}
 *
 * is the link in the ownership-review notification email. It opens the Admin
 * hub → Ownership Review tab with that case selected.
 *
 *   /admin/disclosure-editions
 *
 * opens the Admin hub → Disclosure editions tab (no parameters). Production serves
 * unknown paths with index.html (frontend/nginx.conf `try_files … /index.html`),
 * so a path route is safe.
 *
 * The link is stashed in sessionStorage on load so it survives sign-in: both
 * the login screen (the path stays put) and the 401 handler's hard redirect to
 * "/" for a stale session (the path is lost, the stash is not).
 */

export const ADMIN_OWNERSHIP_REVIEW_PATH = '/admin/ownership-review';
export const ADMIN_DISCLOSURE_EDITIONS_PATH = '/admin/disclosure-editions';

const STASH_KEY = 'cutthe6.com_admin_deep_link';
/** A stashed link is only honored for this long (one sign-in's worth). */
export const ADMIN_DEEP_LINK_MAX_AGE_MS = 30 * 60 * 1000;

/** Tabs a deep link may open (anything else in a stash is dropped). */
const DEEP_LINK_TABS = ['ownership-review', 'disclosure-editions'];

/**
 * @param {{pathname: string, search: string}} location
 * @returns {{tab: 'ownership-review'|'disclosure-editions', caseId: number|null}|null}
 */
export function parseAdminDeepLink(location) {
  if (!location || typeof location.pathname !== 'string') return null;
  const path = location.pathname.replace(/\/+$/, '');
  if (path === ADMIN_DISCLOSURE_EDITIONS_PATH) return { tab: 'disclosure-editions', caseId: null };
  if (path !== ADMIN_OWNERSHIP_REVIEW_PATH) return null;
  const raw = new URLSearchParams(location.search || '').get('case');
  const id = raw != null && /^\d+$/.test(raw) ? Number(raw) : null;
  return { tab: 'ownership-review', caseId: id && id > 0 ? id : null };
}

/** Remember a deep link across sign-in (same tab). */
export function stashAdminDeepLink(link) {
  if (!link) return;
  try {
    sessionStorage.setItem(STASH_KEY, JSON.stringify({ ...link, at: Date.now() }));
  } catch (_) {
    /* storage unavailable — the link still works when already signed in */
  }
}

/** Read and clear the stashed deep link (null when none, expired or malformed). */
export function takeStashedAdminDeepLink() {
  try {
    const raw = sessionStorage.getItem(STASH_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STASH_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || !DEEP_LINK_TABS.includes(parsed.tab)) return null;
    if (typeof parsed.at !== 'number' || Date.now() - parsed.at > ADMIN_DEEP_LINK_MAX_AGE_MS) return null;
    if (parsed.tab !== 'ownership-review') return { tab: parsed.tab, caseId: null };
    const caseId = Number.isInteger(parsed.caseId) && parsed.caseId > 0 ? parsed.caseId : null;
    return { tab: 'ownership-review', caseId };
  } catch (_) {
    return null;
  }
}

/** Drop any stashed link (e.g. once it has been applied). */
export function clearStashedAdminDeepLink() {
  try {
    sessionStorage.removeItem(STASH_KEY);
  } catch (_) {
    /* ignore */
  }
}
